import { useEffect, useState, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { supabase } from '@/services/supabaseClient'
import { handleLogout } from '@/utils/authHelper'
import { TEACHER_DASHBOARD_PATH, isAdminEmail, getCollegeByAdminEmail } from '@/utils/userAccess'
import type { User } from '@supabase/supabase-js'
import EmsLogo from '@/components/EmsLogo'

interface StudentReportRow {
  student_iid?: string
  iid?: string
  session?: string
  group?: string
  class_roll?: string | number
  student_name_en?: string
  student_name_bn?: string
  gender?: string
  mobile?: string
  ssc_roll?: string
}

export default function ResultDashboardPage() {
  const navigate = useNavigate()
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [collegeName, setCollegeName] = useState<string>('')
  const [collegeEiin, setCollegeEiin] = useState<number | string | null>(null)

  // Inline & Modal Edit by IID state
  const [showIidPanel, setShowIidPanel] = useState(false)
  const [showEditIidModal, setShowEditIidModal] = useState(false)
  const [iidInputValue, setIidInputValue] = useState('')
  const [iidInputModalValue, setIidInputModalValue] = useState('')

  // Change Password Modal state
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordMsg, setPasswordMsg] = useState<{ text: string; type: 'success' | 'error' } | null>(null)

  // College Setup Modal state
  const [showCollegeSetupModal, setShowCollegeSetupModal] = useState(false)
  const [setupData, setSetupData] = useState({
    college_name: '',
    college_address: '',
    college_mobile: '',
    college_eiin: '',
    admin_email: '',
    college_logo_url: '',
    website: '',
    email: '',
    principal_name: '',
    college_name_en: '',
    college_address_en: '',
    principal_name_en: ''
  })
  const [setupSaving, setSetupSaving] = useState(false)
  const [setupMsg, setSetupMsg] = useState<{ text: string; type: 'success' | 'error' } | null>(null)

  // Report Modal state
  const [showReportModal, setShowReportModal] = useState(false)
  const [reportSession, setReportSession] = useState('')
  const [reportGender, setReportGender] = useState('')
  const [reportGroup, setReportGroup] = useState('')
  const [reportData, setReportData] = useState<StudentReportRow[]>([])
  const [reportLoading, setReportLoading] = useState(false)

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (!user) {
        sessionStorage.setItem('redirectUrl', '/dashboard')
        navigate('/login', { replace: true })
      } else {
        if (!isAdminEmail(user.email ?? user.id ?? '')) {
          navigate(TEACHER_DASHBOARD_PATH, { replace: true })
          return
        }
        setUser(user)
        setLoading(false)
        if (user.email) {
          getCollegeByAdminEmail(user.email).then(col => {
            if (col) {
              if (col.college_name) setCollegeName(col.college_name)
              if (col.college_eiin) setCollegeEiin(col.college_eiin)
            }
          })
        }
      }
    })
  }, [navigate])

  // Open Edit by IID
  function openIidEdit(iidVal: string) {
    const trimmed = iidVal.trim()
    if (!trimmed) {
      alert('Please enter an IID')
      return
    }

    const targetUrl = `/collegedatabase/login/editdetailsnew.html?iid=${encodeURIComponent(trimmed)}`
    window.open(targetUrl, '_blank', 'noopener')
    setShowEditIidModal(false)
  }

  // Change Password
  async function handleChangePassword(e: React.FormEvent) {
    e.preventDefault()
    if (newPassword !== confirmPassword) {
      setPasswordMsg({ text: 'New passwords do not match!', type: 'error' })
      return
    }
    if (newPassword.length < 6) {
      setPasswordMsg({ text: 'New password must be at least 6 characters long!', type: 'error' })
      return
    }

    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword })
      if (error) throw error
      setPasswordMsg({ text: 'Password updated successfully!', type: 'success' })
      setTimeout(() => {
        setShowPasswordModal(false)
        setPasswordMsg(null)
        setCurrentPassword('')
        setNewPassword('')
        setConfirmPassword('')
      }, 2000)
    } catch (err: any) {
      setPasswordMsg({ text: err.message || 'Failed to update password', type: 'error' })
    }
  }

  /*
  // Open College Setup Modal
  async function handleOpenCollegeSetup() {
    setSetupMsg(null)
    if (user?.email) {
      const col = await getCollegeByAdminEmail(user.email)
      if (col) {
        setSetupData({
          college_name: col.college_name || '',
          college_address: col.college_address || '',
          college_mobile: col.college_mobile || '',
          college_eiin: String(col.college_eiin || ''),
          admin_email: col.admin_email || user.email || '',
          college_logo_url: col.college_logo_url || '',
          website: col.website || '',
          email: col.email || '',
          principal_name: col.principal_name || '',
          college_name_en: col.college_name_en || '',
          college_address_en: col.college_address_en || '',
          principal_name_en: col.principal_name_en || ''
        })
      }
    }
    setShowCollegeSetupModal(true)
  }
  */

  // Save College Setup
  async function handleSaveCollegeSetup(e: React.FormEvent) {
    e.preventDefault()
    if (!user?.email) return
    setSetupSaving(true)
    setSetupMsg(null)

    try {
      const { error } = await supabase
        .from('college_user')
        .update({
          college_name: setupData.college_name.trim(),
          college_address: setupData.college_address.trim(),
          college_mobile: setupData.college_mobile.trim(),
          website: setupData.website.trim(),
          email: setupData.email.trim(),
          principal_name: setupData.principal_name.trim(),
          college_name_en: setupData.college_name_en.trim(),
          college_address_en: setupData.college_address_en.trim(),
          principal_name_en: setupData.principal_name_en.trim()
        })
        .ilike('admin_email', user.email.trim().toLowerCase())

      if (error) throw error

      setCollegeName(setupData.college_name.trim())
      setSetupMsg({ text: 'College profile updated successfully!', type: 'success' })
      setTimeout(() => {
        setShowCollegeSetupModal(false)
        setSetupMsg(null)
      }, 1500)
    } catch (err: any) {
      setSetupMsg({ text: err.message || 'Failed to update college setup', type: 'error' })
    } finally {
      setSetupSaving(false)
    }
  }

  // Load Report Data for Report Modal
  async function loadReportData() {
    if (reportData.length > 0) return
    setReportLoading(true)
    try {
      // Get current logged in user email & college_eiin
      const { data: authData } = await supabase.auth.getUser()
      const userEmail = authData.user?.email
      let collegeEiin: number | string | null = null

      if (userEmail) {
        const { data: colUser } = await supabase
          .from('college_user')
          .select('college_eiin')
          .ilike('admin_email', userEmail.trim().toLowerCase())
          .limit(1)
          .maybeSingle()
        if (colUser?.college_eiin) {
          collegeEiin = colUser.college_eiin
        }
      }

      let allData: StudentReportRow[] = []
      let lastId = 0
      const batchSize = 1000
      let pkCol = 'student_iid'

      while (true) {
        let q = supabase.from('database_fgc').select('*')
        if (collegeEiin) q = q.eq('college_eiin', collegeEiin)
        if (lastId > 0) q = q.gt(pkCol, lastId)
        q = q.order(pkCol, { ascending: true }).limit(batchSize)

        let res = await q
        if (res.error && pkCol === 'student_iid') {
          pkCol = 'iid'
          q = supabase.from('database_fgc').select('*')
          if (collegeEiin) q = q.eq('college_eiin', collegeEiin)
          if (lastId > 0) q = q.gt(pkCol, lastId)
          q = q.order(pkCol, { ascending: true }).limit(batchSize)
          res = await q
        }

        if (res.error) {
          const fallbackRes = await supabase.from('database_fgc').select('*').limit(1000)
          if (!fallbackRes.error && fallbackRes.data) {
            allData = fallbackRes.data
          } else {
            const fallbackRes2 = await supabase.from('student_database').select('*').limit(1000)
            if (!fallbackRes2.error && fallbackRes2.data) {
              allData = fallbackRes2.data
            }
          }
          break
        }

        const data = res.data
        if (!data || data.length === 0) break
        allData = allData.concat(data)

        const lastRow: any = data[data.length - 1]
        const nextId = Number(lastRow.student_iid ?? lastRow.iid ?? 0)
        if (!nextId || nextId === lastId) break
        lastId = nextId
        if (data.length < batchSize) break
      }

      setReportData(allData)
    } catch (e) {
      console.error('Error loading report data', e)
    } finally {
      setReportLoading(false)
    }
  }

  const reportSessions = useMemo(() => {
    const sSet = new Set<string>()
    reportData.forEach(r => { if (r.session) sSet.add(String(r.session).trim()) })
    return Array.from(sSet).sort()
  }, [reportData])

  const reportGroups = useMemo(() => {
    const gSet = new Set<string>()
    reportData.forEach(r => {
      if (!r.group) return
      if (reportSession && String(r.session || '').trim() !== reportSession) return
      gSet.add(String(r.group).trim())
    })
    return Array.from(gSet).sort()
  }, [reportData, reportSession])

  const filteredReportList = useMemo(() => {
    return reportData.filter(r => {
      if (reportSession && String(r.session || '').trim() !== reportSession) return false
      if (reportGender) {
        const gNorm = String(r.gender || '').trim().toLowerCase()
        if (gNorm !== reportGender.toLowerCase() && gNorm !== reportGender.toLowerCase()[0]) return false
      }
      if (reportGroup) {
        const grpNorm = String(r.group || '').trim().toLowerCase()
        if (grpNorm !== reportGroup.toLowerCase()) return false
      }
      return true
    })
  }, [reportData, reportSession, reportGender, reportGroup])

  const reportKpis = useMemo(() => {
    let male = 0, female = 0, science = 0, humanities = 0, business = 0
    filteredReportList.forEach(r => {
      const gNorm = String(r.gender || '').trim().toLowerCase()
      if (gNorm === 'male' || gNorm === 'm') male++
      else if (gNorm === 'female' || gNorm === 'f') female++

      const grp = String(r.group || '').trim().toLowerCase()
      if (grp.includes('science')) science++
      else if (grp.includes('humanities') || grp.includes('arts')) humanities++
      else if (grp.includes('business') || grp.includes('commerce')) business++
    })
    return { total: filteredReportList.length, male, female, science, humanities, business }
  }, [filteredReportList])

  if (loading && !user) return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="spinner" />
    </div>
  )

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: 'radial-gradient(circle at 18% 20%, rgba(37, 99, 235, 0.08), transparent 28%), radial-gradient(circle at 82% 12%, rgba(249, 115, 22, 0.08), transparent 30%), linear-gradient(180deg, #f9fafb, #f3f4f9)',
      color: '#0f172a',
      fontFamily: "'Sora', 'Outfit', system-ui, sans-serif"
    }}>
      {/* HEADER */}
      <header style={{
        position: 'sticky', top: '0.5rem', zIndex: 50,
        margin: '0.5rem 1.5rem 0',
        padding: '1rem 1.5rem',
        background: 'rgba(255, 255, 255, 0.92)',
        backdropFilter: 'blur(12px)',
        borderRadius: '20px',
        border: '1px solid rgba(226, 232, 240, 0.8)',
        boxShadow: '0 18px 50px rgba(15, 23, 42, 0.08)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <EmsLogo size={36} />
          <h1 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.2px' }}>
            কলেজ ড্যাশবোর্ড
          </h1>
        </div>

        <div style={{ color: '#2563eb', fontWeight: 800, fontSize: '14px', letterSpacing: '0.3px' }}>
          Welcome, {user?.email || 'Admin'}
        </div>

        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <button 
            onClick={() => setShowPasswordModal(true)}
            style={{
              background: 'linear-gradient(135deg, #fefefe, #f8fafc)',
              color: '#0f172a',
              border: '1px solid #e5e7eb',
              padding: '0.45rem 1rem',
              borderRadius: '12px',
              cursor: 'pointer',
              fontWeight: 700,
              fontSize: '13px',
              boxShadow: '0 8px 22px rgba(15, 23, 42, 0.08)',
              transition: 'transform 0.15s'
            }}
          >🔑 Change Password</button>

          <button 
            onClick={() => navigate('/setup')}
            style={{
              background: 'linear-gradient(135deg, #fff7ed, #ffedd5)',
              color: '#ea580c',
              border: '1px solid rgba(249, 115, 22, 0.3)',
              padding: '0.45rem 1rem',
              borderRadius: '12px',
              cursor: 'pointer',
              fontWeight: 800,
              fontSize: '13px',
              boxShadow: '0 8px 22px rgba(249, 115, 22, 0.12)',
              transition: 'transform 0.15s'
            }}
          >⚙️ Setup</button>
          <button 
            onClick={() => handleLogout()}
            style={{
              background: 'linear-gradient(135deg, #2563eb, #1d4ed8)',
              color: '#fff',
              border: 'none',
              padding: '0.45rem 1.1rem',
              borderRadius: '12px',
              cursor: 'pointer',
              fontWeight: 700,
              fontSize: '13px',
              boxShadow: '0 12px 30px rgba(37, 99, 235, 0.25)',
              transition: 'transform 0.15s'
            }}
          >Logout</button>
        </div>
      </header>

      {/* CONTAINER & SIDEBAR GRID */}
      <div style={{ maxWidth: '1340px', margin: '0 auto', padding: '1.25rem 1.5rem 2rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '270px 1fr', gap: '1.5rem' }}>
          
          {/* SIDEBAR */}
          <aside style={{
            background: 'linear-gradient(180deg, #ffffff, #f6f8ff)',
            borderRadius: '16px',
            boxShadow: '0 18px 50px rgba(15, 23, 42, 0.08)',
            padding: '1.25rem 1.15rem 1.5rem',
            border: '1px solid #e5e7eb',
            height: 'fit-content'
          }}>
            <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '11px', padding: '4px 10px', borderRadius: '999px', background: 'rgba(37, 99, 235, 0.12)', color: '#2563eb', fontWeight: 700 }}>
                Control Menu
              </span>
            </div>

            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 16px 0' }}>
              <li>
                <a href="#dashboard" style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '0.75rem 0.9rem', color: '#2563eb', textDecoration: 'none',
                  borderRadius: '12px', background: 'rgba(37, 99, 235, 0.08)',
                  borderColor: 'rgba(37, 99, 235, 0.2)', fontWeight: 700,
                  borderLeft: '4px solid #f97316'
                }}>
                  <span><span style={{ marginRight: '10px' }}>📊</span>Dashboard</span>
                </a>
              </li>
            </ul>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <a href="/collegedatabase/login/data_print.html" style={{
                display: 'block', padding: '0.68rem 0.9rem',
                background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.08), rgba(37, 99, 235, 0.02))',
                border: '1px solid rgba(37, 99, 235, 0.22)', borderRadius: '12px',
                color: '#0f172a', textDecoration: 'none', fontWeight: 700, fontSize: '13px'
              }}>🖨️ Data Print</a>

              <a href="/collegedatabase/login/data_edit.html" style={{
                display: 'block', padding: '0.68rem 0.9rem',
                background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.08), rgba(37, 99, 235, 0.02))',
                border: '1px solid rgba(37, 99, 235, 0.22)', borderRadius: '12px',
                color: '#0f172a', textDecoration: 'none', fontWeight: 700, fontSize: '13px'
              }}>📝 Data Edit Colm</a>

              <Link to="/teachers-database" style={{
                display: 'block', padding: '0.68rem 0.9rem',
                background: 'linear-gradient(135deg, rgba(249, 115, 22, 0.08), rgba(249, 115, 22, 0.02))',
                border: '1px solid rgba(249, 115, 22, 0.22)', borderRadius: '12px',
                color: '#ea580c', textDecoration: 'none', fontWeight: 700, fontSize: '13px'
              }}>👨‍🏫 Teachers Database</Link>

              <Link to="/web_admin" style={{
                display: 'block', padding: '0.68rem 0.9rem',
                background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.08), rgba(37, 99, 235, 0.02))',
                border: '1px solid rgba(37, 99, 235, 0.22)', borderRadius: '12px',
                color: '#0f172a', textDecoration: 'none', fontWeight: 700, fontSize: '13px'
              }}>🌐 web admin</Link>

              <button 
                onClick={() => { setShowReportModal(true); loadReportData(); }}
                style={{
                  display: 'block', width: '100%', textAlign: 'left', padding: '0.68rem 0.9rem',
                  background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.12), rgba(37, 99, 235, 0.04))',
                  border: '1px solid rgba(37, 99, 235, 0.3)', borderRadius: '12px',
                  color: '#1d4ed8', fontWeight: 700, fontSize: '13px', cursor: 'pointer'
                }}
              >📊 Report</button>
            </div>
          </aside>

          {/* MAIN CONTENT AREA */}
          <main style={{
            background: 'linear-gradient(180deg, #ffffff, #f9fbff)',
            borderRadius: '16px',
            boxShadow: '0 16px 45px rgba(37, 99, 235, 0.12)',
            padding: '1.5rem',
            border: '1px solid #e5e7eb',
            minHeight: '620px'
          }}>

            {/* COMPRESSED HERO CARD */}
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '0.85rem 1.15rem', borderRadius: '14px',
              background: 'linear-gradient(120deg, rgba(37, 99, 235, 0.06), rgba(249, 115, 22, 0.06))',
              border: '1px solid rgba(37, 99, 235, 0.12)',
              boxShadow: '0 8px 24px rgba(15, 23, 42, 0.04)',
              marginBottom: '1rem',
              flexWrap: 'wrap',
              gap: '12px'
            }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '3px' }}>
                  <span style={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '10px', color: '#ff5500', fontWeight: 800 }}>CONTROL CENTER</span>
                  {collegeEiin && (
                    <span style={{ padding: '2px 8px', borderRadius: '999px', fontSize: '10px', fontWeight: 800, background: 'rgba(37, 99, 235, 0.1)', color: '#2563eb' }}>
                      EIIN: {collegeEiin}
                    </span>
                  )}
                </div>
                <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 900, color: '#0f172a' }}>
                  {collegeName || 'ফেনী সাউথ-ইস্ট কলেজ'}
                </h2>
              </div>

              <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ padding: '4px 10px', borderRadius: '8px', fontSize: '11px', fontWeight: 700, background: '#fff', border: '1px solid #e5e7eb', color: '#0f172a' }}>
                  Database Live
                </span>
                <span style={{ padding: '4px 10px', borderRadius: '8px', fontSize: '11px', fontWeight: 700, background: '#fff', border: '1px solid #e5e7eb', color: '#2563eb' }}>
                  IID Quick Edit
                </span>
              </div>
            </div>

            {/* SECTION HEADER */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 800 }}>Grouped Actions</h2>
            </div>

            {/* GROUPED ACTIONS CLUSTER GRID */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
              
              {/* DATA MANAGEMENT CARD */}
              <div style={{ background: '#fff', borderRadius: '16px', padding: '1rem 1.05rem', border: '1px solid #e5e7eb', boxShadow: '0 12px 32px rgba(15, 23, 42, 0.06)', display: 'grid', gap: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 800 }}>Data Management</h3>
                  <span style={{ padding: '4px 10px', borderRadius: '999px', background: 'rgba(249, 115, 22, 0.14)', color: '#f97316', fontWeight: 700, fontSize: '11px' }}>Core</span>
                </div>
                <div style={{ display: 'grid', gap: '8px' }}>
                  <Link to="/datalist" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: 'linear-gradient(180deg, #f8fbff 0%, #eef4ff 100%)', borderRadius: '12px', textDecoration: 'none', color: '#0f172a', fontWeight: 700, fontSize: '13px', border: '1px solid rgba(37, 99, 235, 0.1)' }}>
                    <span><span style={{ marginRight: '8px' }}>📚</span>Database All</span>
                    <span style={{ padding: '4px 8px', borderRadius: '8px', fontSize: '11px', color: '#fff', background: '#f97316' }}>Working</span>
                  </Link>
                  <Link to="/datalist-subject" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: 'linear-gradient(180deg, #f8fbff 0%, #eef4ff 100%)', borderRadius: '12px', textDecoration: 'none', color: '#0f172a', fontWeight: 700, fontSize: '13px', border: '1px solid rgba(37, 99, 235, 0.1)' }}>
                    <span><span style={{ marginRight: '8px' }}>📚</span>Subject</span>
                    <span style={{ padding: '4px 8px', borderRadius: '8px', fontSize: '11px', color: '#fff', background: '#f97316' }}>Working</span>
                  </Link>
                  <Link to="/session-subjects" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: 'linear-gradient(180deg, #fff7ed 0%, #ffedd5 100%)', borderRadius: '12px', textDecoration: 'none', color: '#0f172a', fontWeight: 700, fontSize: '13px', border: '1px solid rgba(249, 115, 22, 0.2)' }}>
                    <span><span style={{ marginRight: '8px' }}>📖</span>Session Subjects</span>
                    <span style={{ padding: '4px 8px', borderRadius: '8px', fontSize: '11px', color: '#fff', background: '#ff7a00' }}>Manage</span>
                  </Link>
                  <Link to="/testimonial" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: 'linear-gradient(180deg, #fff7ed 0%, #ffedd5 100%)', borderRadius: '12px', textDecoration: 'none', color: '#0f172a', fontWeight: 700, fontSize: '13px', border: '1px solid rgba(249, 115, 22, 0.2)' }}>
                    <span><span style={{ marginRight: '8px' }}>📜</span>প্রত্যয়ন পত্র (Testimonial)</span>
                    <span style={{ padding: '4px 8px', borderRadius: '8px', fontSize: '11px', color: '#fff', background: '#ff7a00' }}>Approve</span>
                  </Link>
                </div>
              </div>

              {/* RESULT & EXAM CONTROL CARD */}
              <div style={{ background: '#fff', borderRadius: '16px', padding: '1rem 1.05rem', border: '1px solid #e5e7eb', boxShadow: '0 12px 32px rgba(15, 23, 42, 0.06)', display: 'grid', gap: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 800 }}>Result</h3>
                  <span style={{ padding: '4px 10px', borderRadius: '999px', background: 'rgba(37, 99, 235, 0.14)', color: '#2563eb', fontWeight: 700, fontSize: '11px' }}>Exam</span>
                </div>
                <div style={{ display: 'grid', gap: '8px' }}>
                  {/* EXAM CONTROL BUTTON -> NAVIGATES TO EXAM DASHBOARD / EXAM MANAGER */}
                  <Link 
                    to="/exam-control" 
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '12px 12px',
                      background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                      borderRadius: '12px', textDecoration: 'none', color: '#1d4ed8',
                      fontWeight: 800, fontSize: '14px',
                      border: '1.5px solid rgba(37, 99, 235, 0.3)',
                      boxShadow: '0 6px 18px rgba(37, 99, 235, 0.12)',
                      transition: 'transform 0.12s ease'
                    }}
                  >
                    <span><span style={{ marginRight: '8px', fontSize: '16px' }}>⚡</span>exam controll</span>
                    <span style={{ padding: '4px 10px', borderRadius: '8px', fontSize: '11px', color: '#fff', background: 'linear-gradient(135deg, #2563eb, #1d4ed8)', fontWeight: 800 }}>Running Now</span>
                  </Link>
                </div>
              </div>

              {/* ADD NEW & EDIT CARD */}
              <div style={{ background: '#fff', borderRadius: '16px', padding: '1rem 1.05rem', border: '1px solid #e5e7eb', boxShadow: '0 12px 32px rgba(15, 23, 42, 0.06)', display: 'grid', gap: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 800 }}>Add New & Edit</h3>
                  <span style={{ padding: '4px 10px', borderRadius: '999px', background: 'rgba(16, 185, 129, 0.14)', color: '#059669', fontWeight: 700, fontSize: '11px' }}>Actions</span>
                </div>
                <div style={{ display: 'grid', gap: '8px' }}>
                  <Link to="/add-student" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: 'linear-gradient(180deg, #f8fbff 0%, #eef4ff 100%)', borderRadius: '12px', textDecoration: 'none', color: '#0f172a', fontWeight: 700, fontSize: '13px', border: '1px solid rgba(37, 99, 235, 0.1)' }}>
                    <span><span style={{ marginRight: '8px' }}>➕</span>Add New Student</span>
                  </Link>
                  <button 
                    onClick={() => setShowEditIidModal(true)}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: 'linear-gradient(180deg, #fff7ed 0%, #ffedd5 100%)', borderRadius: '12px', textDecoration: 'none', color: '#ea580c', fontWeight: 700, fontSize: '13px', border: '1px solid rgba(249, 115, 22, 0.2)', width: '100%', cursor: 'pointer', textAlign: 'left' }}
                  >
                    <span><span style={{ marginRight: '8px' }}>📝</span>Edit by IID</span>
                    <span style={{ padding: '4px 8px', borderRadius: '8px', fontSize: '11px', color: '#fff', background: '#f97316' }}>New</span>
                  </button>
                  <a href="/collegedatabase/login/student_data_update.html" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: 'linear-gradient(180deg, #f8fbff 0%, #eef4ff 100%)', borderRadius: '12px', textDecoration: 'none', color: '#0f172a', fontWeight: 700, fontSize: '13px', border: '1px solid rgba(37, 99, 235, 0.1)' }}>
                    <span><span style={{ marginRight: '8px' }}>📄</span>Subject view</span>
                  </a>
                  <a href="/collegedatabase/login/student_data_subject_Export.html" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: 'linear-gradient(180deg, #f8fbff 0%, #eef4ff 100%)', borderRadius: '12px', textDecoration: 'none', color: '#0f172a', fontWeight: 700, fontSize: '13px', border: '1px solid rgba(37, 99, 235, 0.1)' }}>
                    <span><span style={{ marginRight: '8px' }}>📄</span>Export</span>
                  </a>
                </div>
              </div>

            </div>

            {/* INLINE QUICK IID EDIT PANEL */}
            {showIidPanel && (
              <div style={{
                margin: '1.25rem 0', padding: '1.15rem', borderRadius: '16px',
                background: 'linear-gradient(135deg, #fffaf5 0%, #fff7ed 100%)',
                border: '1px solid rgba(249, 115, 22, 0.35)',
                boxShadow: '0 16px 38px rgba(249, 115, 22, 0.12)'
              }}>
                <div style={{ fontWeight: 800, fontSize: '16px', marginBottom: '12px', color: '#9a3412' }}>Edit Student by IID</div>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '12px' }}>
                  <label style={{ fontWeight: 700, fontSize: '14px', color: '#374151' }}>IID:</label>
                  <input 
                    type="text" 
                    placeholder="e.g. 106584230001" 
                    maxLength={12} 
                    value={iidInputValue}
                    onChange={(e) => setIidInputValue(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') openIidEdit(iidInputValue) }}
                    style={{ flex: 1, padding: '10px 14px', borderRadius: '10px', border: '1px solid #f97316', outline: 'none', fontSize: '14px', background: '#fff' }}
                  />
                  <button 
                    onClick={() => openIidEdit(iidInputValue)}
                    style={{ padding: '10px 18px', background: '#ef6c00', color: '#fff', border: 'none', borderRadius: '10px', fontWeight: 800, cursor: 'pointer' }}
                  >🔍 Open Edit</button>
                  <button 
                    onClick={() => setShowIidPanel(false)}
                    style={{ padding: '10px 14px', background: '#fff', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '10px', cursor: 'pointer', fontWeight: 600 }}
                  >Close</button>
                </div>
              </div>
            )}

          </main>
        </div>
      </div>

      {/* EDIT BY IID MODAL */}
      {showEditIidModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', padding: '2rem', borderRadius: '16px', width: '100%', maxWidth: '440px', boxShadow: '0 20px 40px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 800 }}>Edit Student by IID</h3>
              <button onClick={() => setShowEditIidModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer' }}>&times;</button>
            </div>
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 700, fontSize: '13px' }}>IID (12 digits or ID)</label>
              <input 
                type="text" 
                placeholder="e.g. 106584230001" 
                maxLength={12}
                value={iidInputModalValue}
                onChange={e => setIidInputModalValue(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') openIidEdit(iidInputModalValue) }}
                style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid #e5e7eb', boxSizing: 'border-box', outline: 'none' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button 
                onClick={() => openIidEdit(iidInputModalValue)}
                style={{ flex: 1, background: '#ef6c00', color: '#fff', border: 'none', padding: '10px', borderRadius: '10px', fontWeight: 800, cursor: 'pointer' }}
              >Open Edit</button>
              <button 
                onClick={() => setShowEditIidModal(false)}
                style={{ background: '#fff', border: '1px solid #e5e7eb', padding: '10px 16px', borderRadius: '10px', cursor: 'pointer', fontWeight: 600 }}
              >Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* CHANGE PASSWORD MODAL */}
      {showPasswordModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', padding: '2rem', borderRadius: '16px', width: '100%', maxWidth: '440px', boxShadow: '0 20px 40px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 800 }}>Change Password</h3>
              <button onClick={() => setShowPasswordModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer' }}>&times;</button>
            </div>

            {passwordMsg && (
              <div style={{
                padding: '0.75rem', borderRadius: '8px', marginBottom: '1rem', fontWeight: 600, fontSize: '13px',
                background: passwordMsg.type === 'success' ? '#d1fae5' : '#fee2e2',
                color: passwordMsg.type === 'success' ? '#065f46' : '#991b1b',
                border: `1px solid ${passwordMsg.type === 'success' ? '#a7f3d0' : '#fca5a5'}`
              }}>
                {passwordMsg.text}
              </div>
            )}

            <form onSubmit={handleChangePassword}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.4rem', fontWeight: 700, fontSize: '13px' }}>Current Password</label>
                <input 
                  type="password" 
                  required 
                  value={currentPassword}
                  onChange={e => setCurrentPassword(e.target.value)}
                  style={{ width: '100%', padding: '9px 12px', borderRadius: '10px', border: '1px solid #e5e7eb', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.4rem', fontWeight: 700, fontSize: '13px' }}>New Password</label>
                <input 
                  type="password" 
                  required 
                  minLength={6}
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  style={{ width: '100%', padding: '9px 12px', borderRadius: '10px', border: '1px solid #e5e7eb', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.4rem', fontWeight: 700, fontSize: '13px' }}>Confirm New Password</label>
                <input 
                  type="password" 
                  required 
                  minLength={6}
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  style={{ width: '100%', padding: '9px 12px', borderRadius: '10px', border: '1px solid #e5e7eb', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button type="submit" style={{ flex: 1, background: '#2563eb', color: '#fff', border: 'none', padding: '10px', borderRadius: '10px', fontWeight: 800, cursor: 'pointer' }}>Update Password</button>
                <button type="button" onClick={() => setShowPasswordModal(false)} style={{ background: '#fff', border: '1px solid #e5e7eb', padding: '10px 16px', borderRadius: '10px', cursor: 'pointer', fontWeight: 600 }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* STUDENT ANALYTICS & REPORT MODAL */}
      {showReportModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', padding: '1.5rem', borderRadius: '16px', width: '100%', maxWidth: '920px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 40px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e5e7eb', paddingBottom: '0.8rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 800, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>📊 Student Analytics & Report</span>
              </h3>
              <button onClick={() => setShowReportModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer' }}>&times;</button>
            </div>

            {/* FILTERS SECTION */}
            <div style={{ background: '#f8fafc', padding: '1rem', borderRadius: '12px', border: '1px solid #e2e8f0', margin: '1rem 0' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '12px', alignItems: 'end' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, marginBottom: '4px' }}>Session (সেশন)</label>
                  <select 
                    value={reportSession}
                    onChange={e => setReportSession(e.target.value)}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#fff' }}
                  >
                    <option value="">All Sessions</option>
                    {reportSessions.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, marginBottom: '4px' }}>Gender (লিঙ্গ)</label>
                  <select 
                    value={reportGender}
                    onChange={e => setReportGender(e.target.value)}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#fff' }}
                  >
                    <option value="">All Genders</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, marginBottom: '4px' }}>Group (গ্রুপ)</label>
                  <select 
                    value={reportGroup}
                    onChange={e => setReportGroup(e.target.value)}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#fff' }}
                  >
                    <option value="">All Groups</option>
                    {reportGroups.map(g => <option key={g} value={g}>{g}</option>)}
                  </select>
                </div>

                <div style={{ display: 'flex', gap: '8px' }}>
                  <button 
                    onClick={loadReportData}
                    style={{ flex: 1, padding: '8px 14px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 700, cursor: 'pointer' }}
                  >Generate</button>
                  <button 
                    onClick={() => window.print()}
                    style={{ padding: '8px 14px', background: '#fff', border: '1px solid #cbd5e1', borderRadius: '8px', fontWeight: 700, cursor: 'pointer' }}
                  >🖨️ Print</button>
                </div>
              </div>
            </div>

            {/* KPI CARDS */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '10px', marginBottom: '1rem' }}>
              <div style={{ padding: '10px', borderRadius: '10px', background: '#eff6ff', border: '1px solid #bfdbfe', textAlign: 'center' }}>
                <small style={{ color: '#1e40af', fontWeight: 700, display: 'block', fontSize: '11px' }}>Total</small>
                <strong style={{ color: '#1d4ed8', fontSize: '1.3rem' }}>{reportKpis.total}</strong>
              </div>
              <div style={{ padding: '10px', borderRadius: '10px', background: '#f0fdf4', border: '1px solid #bbf7d0', textAlign: 'center' }}>
                <small style={{ color: '#166534', fontWeight: 700, display: 'block', fontSize: '11px' }}>Male</small>
                <strong style={{ color: '#15803d', fontSize: '1.3rem' }}>{reportKpis.male}</strong>
              </div>
              <div style={{ padding: '10px', borderRadius: '10px', background: '#fdf2f8', border: '1px solid #fbcfe8', textAlign: 'center' }}>
                <small style={{ color: '#9d174d', fontWeight: 700, display: 'block', fontSize: '11px' }}>Female</small>
                <strong style={{ color: '#be185d', fontSize: '1.3rem' }}>{reportKpis.female}</strong>
              </div>
              <div style={{ padding: '10px', borderRadius: '10px', background: '#fff7ed', border: '1px solid #fed7aa', textAlign: 'center' }}>
                <small style={{ color: '#9a3412', fontWeight: 700, display: 'block', fontSize: '11px' }}>Science</small>
                <strong style={{ color: '#c2410c', fontSize: '1.3rem' }}>{reportKpis.science}</strong>
              </div>
              <div style={{ padding: '10px', borderRadius: '10px', background: '#faf5ff', border: '1px solid #e9d5ff', textAlign: 'center' }}>
                <small style={{ color: '#6b21a8', fontWeight: 700, display: 'block', fontSize: '11px' }}>Humanities</small>
                <strong style={{ color: '#7e22ce', fontSize: '1.3rem' }}>{reportKpis.humanities}</strong>
              </div>
              <div style={{ padding: '10px', borderRadius: '10px', background: '#f0fdfa', border: '1px solid #99f6e4', textAlign: 'center' }}>
                <small style={{ color: '#115e59', fontWeight: 700, display: 'block', fontSize: '11px' }}>Business</small>
                <strong style={{ color: '#0f766e', fontSize: '1.3rem' }}>{reportKpis.business}</strong>
              </div>
            </div>

            {/* REPORT TABLE */}
            <div style={{ overflowX: 'auto', maxHeight: '400px', border: '1px solid #e2e8f0', borderRadius: '10px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead style={{ background: '#f1f5f9', position: 'sticky', top: 0, zIndex: 2 }}>
                  <tr>
                    <th style={{ padding: '8px', border: '1px solid #e2e8f0', textAlign: 'center' }}>SL</th>
                    <th style={{ padding: '8px', border: '1px solid #e2e8f0' }}>Session</th>
                    <th style={{ padding: '8px', border: '1px solid #e2e8f0' }}>Group</th>
                    <th style={{ padding: '8px', border: '1px solid #e2e8f0' }}>Roll</th>
                    <th style={{ padding: '8px', border: '1px solid #e2e8f0' }}>Name</th>
                    <th style={{ padding: '8px', border: '1px solid #e2e8f0' }}>Gender</th>
                    <th style={{ padding: '8px', border: '1px solid #e2e8f0' }}>Mobile</th>
                    <th style={{ padding: '8px', border: '1px solid #e2e8f0' }}>SSC Roll</th>
                  </tr>
                </thead>
                <tbody>
                  {reportLoading ? (
                    <tr><td colSpan={8} style={{ padding: '16px', textAlign: 'center' }}>Loading data...</td></tr>
                  ) : filteredReportList.length === 0 ? (
                    <tr><td colSpan={8} style={{ padding: '16px', textAlign: 'center', color: '#6b7280' }}>No records found matching filters</td></tr>
                  ) : (
                    filteredReportList.map((r, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid #e2e8f0' }}>
                        <td style={{ padding: '7px 8px', border: '1px solid #e2e8f0', textAlign: 'center' }}>{idx + 1}</td>
                        <td style={{ padding: '7px 8px', border: '1px solid #e2e8f0' }}>{r.session || ''}</td>
                        <td style={{ padding: '7px 8px', border: '1px solid #e2e8f0' }}>{r.group || ''}</td>
                        <td style={{ padding: '7px 8px', border: '1px solid #e2e8f0' }}>{r.class_roll || ''}</td>
                        <td style={{ padding: '7px 8px', border: '1px solid #e2e8f0', fontWeight: 600 }}>{r.student_name_en || r.student_name_bn || ''}</td>
                        <td style={{ padding: '7px 8px', border: '1px solid #e2e8f0' }}>{r.gender || ''}</td>
                        <td style={{ padding: '7px 8px', border: '1px solid #e2e8f0' }}>{r.mobile || ''}</td>
                        <td style={{ padding: '7px 8px', border: '1px solid #e2e8f0' }}>{r.ssc_roll || ''}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

          </div>
        </div>
      )}

      {/* COLLEGE SETUP MODAL */}
      {showCollegeSetupModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.65)', backdropFilter: 'blur(6px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            background: '#ffffff', width: '480px', maxWidth: '92%', borderRadius: '24px',
            padding: '28px', border: '1px solid #e2e8f0', boxShadow: '0 25px 60px rgba(0, 0, 0, 0.25)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 900, color: '#0f172a' }}>
                ⚙️ College Profile Setup
              </h3>
              <button 
                onClick={() => setShowCollegeSetupModal(false)}
                style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#64748b' }}
              >&times;</button>
            </div>

            <form onSubmit={handleSaveCollegeSetup}>
              <div style={{ display: 'grid', gap: '12px', marginBottom: '20px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 800, color: '#64748b', marginBottom: '4px' }}>
                    College EIIN (Non-editable)
                  </label>
                  <input 
                    type="text" 
                    value={setupData.college_eiin} 
                    disabled 
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '10px', border: '1px solid #cbd5e1', background: '#f1f5f9', color: '#64748b', fontWeight: 700, fontSize: '13px', cursor: 'not-allowed', boxSizing: 'border-box' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 800, color: '#64748b', marginBottom: '4px' }}>
                    Admin Email (Non-editable)
                  </label>
                  <input 
                    type="text" 
                    value={setupData.admin_email} 
                    disabled 
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '10px', border: '1px solid #cbd5e1', background: '#f1f5f9', color: '#64748b', fontWeight: 700, fontSize: '13px', cursor: 'not-allowed', boxSizing: 'border-box' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 800, color: '#64748b', marginBottom: '4px' }}>
                    College Logo URL (Non-editable)
                  </label>
                  <input 
                    type="text" 
                    value={setupData.college_logo_url || 'N/A'} 
                    disabled 
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '10px', border: '1px solid #cbd5e1', background: '#f1f5f9', color: '#64748b', fontWeight: 700, fontSize: '13px', cursor: 'not-allowed', boxSizing: 'border-box' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 800, color: '#0f172a', marginBottom: '4px' }}>
                    College Name *
                  </label>
                  <input 
                    type="text" 
                    value={setupData.college_name} 
                    onChange={e => setSetupData({ ...setupData, college_name: e.target.value })} 
                    placeholder="Enter College Name"
                    required
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '14px', boxSizing: 'border-box', outline: 'none' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 800, color: '#0f172a', marginBottom: '4px' }}>
                    College Name (English)
                  </label>
                  <input 
                    type="text" 
                    value={setupData.college_name_en} 
                    onChange={e => setSetupData({ ...setupData, college_name_en: e.target.value })} 
                    placeholder="e.g. Feni South-East College, Feni"
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '14px', boxSizing: 'border-box', outline: 'none' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 800, color: '#0f172a', marginBottom: '4px' }}>
                    Principal Name (Bangla)
                  </label>
                  <input 
                    type="text" 
                    value={setupData.principal_name} 
                    onChange={e => setSetupData({ ...setupData, principal_name: e.target.value })} 
                    placeholder="অধ্যাপক মোহাম্মদ এনামুল হক খন্দকার"
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '14px', boxSizing: 'border-box', outline: 'none' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 800, color: '#0f172a', marginBottom: '4px' }}>
                    Principal Name (English)
                  </label>
                  <input 
                    type="text" 
                    value={setupData.principal_name_en} 
                    onChange={e => setSetupData({ ...setupData, principal_name_en: e.target.value })} 
                    placeholder="Professor Mohammad Enamul Hoque Khondakar"
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '14px', boxSizing: 'border-box', outline: 'none' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 800, color: '#0f172a', marginBottom: '4px' }}>
                    College Mobile
                  </label>
                  <input 
                    type="text" 
                    value={setupData.college_mobile} 
                    onChange={e => setSetupData({ ...setupData, college_mobile: e.target.value })} 
                    placeholder="Enter Mobile Number"
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '14px', boxSizing: 'border-box', outline: 'none' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 800, color: '#0f172a', marginBottom: '4px' }}>
                    College Contact Email
                  </label>
                  <input 
                    type="email" 
                    value={setupData.email} 
                    onChange={e => setSetupData({ ...setupData, email: e.target.value })} 
                    placeholder="college@school.edu"
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '14px', boxSizing: 'border-box', outline: 'none' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 800, color: '#0f172a', marginBottom: '4px' }}>
                    Website URL
                  </label>
                  <input 
                    type="text" 
                    value={setupData.website} 
                    onChange={e => setSetupData({ ...setupData, website: e.target.value })} 
                    placeholder="https://www.college.edu.bd"
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '14px', boxSizing: 'border-box', outline: 'none' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 800, color: '#0f172a', marginBottom: '4px' }}>
                    College Address (Bangla)
                  </label>
                  <input 
                    type="text" 
                    value={setupData.college_address} 
                    onChange={e => setSetupData({ ...setupData, college_address: e.target.value })} 
                    placeholder="এস এস কে রোড, ফেনী"
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '14px', boxSizing: 'border-box', outline: 'none' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 800, color: '#0f172a', marginBottom: '4px' }}>
                    College Address (English)
                  </label>
                  <input 
                    type="text" 
                    value={setupData.college_address_en} 
                    onChange={e => setSetupData({ ...setupData, college_address_en: e.target.value })} 
                    placeholder="SSK Road, Feni"
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '14px', boxSizing: 'border-box', outline: 'none' }}
                  />
                </div>
              </div>

              {setupMsg && (
                <div style={{
                  padding: '10px 14px', borderRadius: '10px', fontSize: '13px', fontWeight: 700, marginBottom: '14px',
                  background: setupMsg.type === 'success' ? '#dcfce7' : '#fee2e2',
                  color: setupMsg.type === 'success' ? '#15803d' : '#b91c1c'
                }}>
                  {setupMsg.text}
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button 
                  type="button" 
                  onClick={() => setShowCollegeSetupModal(false)}
                  style={{ padding: '9px 18px', borderRadius: '10px', border: '1px solid #cbd5e1', background: '#f8fafc', color: '#475569', fontWeight: 700, cursor: 'pointer', fontSize: '13px' }}
                >Cancel</button>
                <button 
                  type="submit" 
                  disabled={setupSaving}
                  style={{ padding: '9px 22px', borderRadius: '10px', border: 'none', background: 'linear-gradient(135deg, #ff7a00, #ff5500)', color: '#fff', fontWeight: 800, cursor: 'pointer', fontSize: '13px' }}
                >{setupSaving ? 'Saving...' : 'Save Profile Setup'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* FOOTER */}
      <footer style={{ padding: '30px', textAlign: 'center', color: '#94a3b8', fontSize: '13px', borderTop: '1px solid #e2e8f0', marginTop: '40px' }}>
        &copy; 2026 Feni South-East College. All rights reserved. FMHS Result & College Portal
      </footer>
    </div>
  )
}
